/**
 * 搜索供应商管理页面
 *
 * 显示搜索供应商使用统计和管理设置，包括：
 * - 概览统计卡片
 * - 趋势分析
 * - 统计仪表盘
 * - 实时监控
 * - 配置管理
 * - 配额管理
 * - 告警配置
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AdminLayout } from '../components/layout/AdminLayout';
import { searchAdminApi, type SearchOverviewData, type UserQuotaQueryResponse } from '../services/searchAdminApi';
import { ProviderStatusCards } from '../components/search/SearchOverview/ProviderStatusCards';
import { QuotaSummary } from '../components/search/SearchOverview/QuotaSummary';
import { HealthStatusChart } from '../components/search/SearchOverview/HealthStatusChart';
import { SearchSettingsEditor } from '../components/search/SearchSettingsEditor';
import { SearchQuotaManager } from '../components/search/SearchQuotaManager';
import { SearchStatsDashboard } from '../components/search/SearchStatsDashboard';
import { SearchLiveMonitor } from '../components/search/SearchLiveMonitor';
import type { SearchProviderStats } from '../services/searchAdminApi';

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  color?: 'blue' | 'green' | 'yellow' | 'red' | 'gray';
}

const StatCard: React.FC<StatCardProps> = ({ title, value, subtitle, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    gray: 'bg-gray-50 border-gray-200 text-gray-700',
  };

  return (
    <div className={`p-4 rounded-lg border ${colorClasses[color]}`}>
      <div className="text-sm font-medium opacity-80">{title}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
      {subtitle && <div className="text-xs mt-1 opacity-60">{subtitle}</div>}
    </div>
  );
};

interface UserQuotaQueryPanelProps {
  onQuery: (userId: number, date: string) => Promise<void>;
  loading: boolean;
}

const UserQuotaQueryPanel: React.FC<UserQuotaQueryPanelProps> = ({ onQuery, loading }) => {
  const [userIdInput, setUserIdInput] = useState<string>('1');
  const [dateInput, setDateInput] = useState<string>(
    new Date().toISOString().split('T')[0]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const userId = parseInt(userIdInput, 10);
    if (userId > 0) {
      onQuery(userId, dateInput);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex gap-2 items-end">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          用户 ID
        </label>
        <input
          type="text"
          value={userIdInput}
          onChange={(e) => setUserIdInput(e.target.value)}
          className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
          placeholder="用户ID"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          日期
        </label>
        <input
          type="date"
          value={dateInput}
          onChange={(e) => setDateInput(e.target.value)}
          className="w-40 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400 text-sm"
      >
        {loading ? '查询中...' : '查询'}
      </button>
    </form>
  );
};

interface UserQuotaResultPanelProps {
  data: UserQuotaQueryResponse | null;
}

const UserQuotaResultPanel: React.FC<UserQuotaResultPanelProps> = ({ data }) => {
  if (!data) {
    return (
      <div className="text-center py-4 text-gray-500 text-sm">
        请输入用户ID和日期进行查询
      </div>
    );
  }

  const usagePercent = data.used > 0
    ? Math.round((data.used / data.daily_limit) * 100)
    : 0;

  const progressColor = usagePercent >= 90 ? 'bg-red-500' : usagePercent >= 70 ? 'bg-yellow-500' : 'bg-green-500';

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-4">
        <div className="p-3 bg-gray-50 rounded-lg">
          <div className="text-xs text-gray-500">用户ID</div>
          <div className="text-lg font-semibold text-gray-800">{data.user_id}</div>
        </div>
        <div className="p-3 bg-gray-50 rounded-lg">
          <div className="text-xs text-gray-500">每日限额</div>
          <div className="text-lg font-semibold text-gray-800">{data.daily_limit} 次</div>
        </div>
        <div className="p-3 bg-gray-50 rounded-lg">
          <div className="text-xs text-gray-500">已使用</div>
          <div className="text-lg font-semibold text-blue-700">{data.used} 次</div>
        </div>
        <div className="p-3 bg-gray-50 rounded-lg">
          <div className="text-xs text-gray-500">剩余额度</div>
          <div className={`text-lg font-semibold ${data.remaining === 0 ? 'text-red-700' : 'text-green-700'}`}>
            {data.remaining} 次
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="text-sm text-gray-600">使用进度:</div>
        <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full ${progressColor} transition-all duration-150`}
            style={{ width: `${Math.min(usagePercent, 100)}%` }}
          />
        </div>
        <div className="text-sm text-gray-600">{usagePercent}%</div>
      </div>

      {data.records.length > 0 ? (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-2 font-medium text-gray-600">时间</th>
                <th className="text-left p-2 font-medium text-gray-600">搜索词</th>
                <th className="text-left p-2 font-medium text-gray-600">类型</th>
                <th className="text-right p-2 font-medium text-gray-600">结果数</th>
                <th className="text-left p-2 font-medium text-gray-600">工作区</th>
              </tr>
            </thead>
            <tbody>
              {data.records.slice(0, 10).map((record, idx) => (
                <tr key={record.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="p-2 text-gray-500 text-xs">
                    {record.created_at ? new Date(record.created_at).toLocaleTimeString() : '-'}
                  </td>
                  <td className="p-2 truncate max-w-[200px]">{record.query}</td>
                  <td className="p-2">
                    <span className={`px-2 py-1 rounded text-xs ${
                      record.search_type === 'novel' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {record.search_type}
                    </span>
                  </td>
                  <td className="text-right p-2">{record.results_count}</td>
                  <td className="p-2 text-gray-500">{record.workspace_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.records.length > 10 && (
            <div className="text-center py-2 text-gray-500 text-xs bg-gray-50">
              显示前 10 条记录，共 {data.records.length} 条
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-4 text-gray-500 text-sm bg-gray-50 rounded-lg">
          该日期无搜索记录
        </div>
      )}
    </div>
  );
};

type TabType = 'overview' | 'trends' | 'statistics' | 'monitoring' | 'settings' | 'quota' | 'alerts';

const TABS: { key: TabType; label: string }[] = [
  { key: 'overview', label: '概览' },
  { key: 'trends', label: '趋势分析' },
  { key: 'statistics', label: '统计仪表盘' },
  { key: 'monitoring', label: '实时监控' },
  { key: 'settings', label: '配置管理' },
  { key: 'quota', label: '配额管理' },
  { key: 'alerts', label: '告警配置' },
];

export const SearchAdminPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = useMemo<TabType>(() => {
    const tab = searchParams.get('tab') as TabType;
    return TABS.some((t) => t.key === tab) ? tab : 'overview';
  }, [searchParams]);

  const handleTabChange = useCallback(
    (tab: TabType) => {
      setSearchParams((prev) => {
        prev.set('tab', tab);
        return prev;
      });
    },
    [setSearchParams]
  );

  const [overview, setOverview] = useState<SearchOverviewData | null>(null);
  const [providerStats, setProviderStats] = useState<SearchProviderStats[]>([]);
  const [quotaData, setQuotaData] = useState<Record<string, Record<string, unknown[]>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [userQuotaData, setUserQuotaData] = useState<UserQuotaQueryResponse | null>(null);
  const [userQuotaLoading, setUserQuotaLoading] = useState(false);

  const handleUserQuotaQuery = useCallback(async (userId: number, date: string) => {
    setUserQuotaLoading(true);
    try {
      const result = await searchAdminApi.queryUserQuota(undefined, { user_id: userId, date });
      setUserQuotaData(result.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : '查询用户配额失败');
    } finally {
      setUserQuotaLoading(false);
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [ov, ps, q] = await Promise.all([
        searchAdminApi.getOverviewStats(),
        searchAdminApi.getProviderStats(),
        searchAdminApi.getQuotaUsage(),
      ]);
      setOverview(ov.data);
      setProviderStats(ps.data);
      setQuotaData(q.quotas as Record<string, Record<string, unknown[]>>);
      setLastUpdate(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取统计数据失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab !== 'overview') return;
    fetchData();
    if (!autoRefresh) return;
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData, autoRefresh, activeTab]);

  const handleExportJson = async () => {
    try {
      const data = await searchAdminApi.exportJson();
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `search-config-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : '导出失败');
    }
  };

  const handleExportCsv = async () => {
    try {
      const blob = await searchAdminApi.exportCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `search-providers-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : '导出失败');
    }
  };

  const handleHealthCheck = async () => {
    try {
      await searchAdminApi.triggerHealthCheck(undefined);
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : '健康检查失败');
    }
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              搜索供应商管理
            </h1>
            {lastUpdate && (
              <p className="text-sm text-gray-500 mt-1">
                最后更新: {lastUpdate.toLocaleTimeString()}
              </p>
            )}
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
              />
              自动刷新
            </label>
            <button
              onClick={handleHealthCheck}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2 text-sm"
            >
              健康检查
            </button>
            <button
              onClick={handleExportCsv}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
            >
              导出 CSV
            </button>
            <button
              onClick={handleExportJson}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
            >
              导出 JSON
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
            <button
              onClick={() => setError('')}
              className="ml-4 text-red-500 hover:text-red-700"
            >
              关闭
            </button>
          </div>
        )}

        <div className="flex gap-1 mb-6 border-b border-gray-200">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                activeTab === tab.key
                  ? 'bg-white border border-b-white border-gray-200 text-blue-600 -mb-px'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <>
            {overview && (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
                <StatCard
                  title="供应商总数"
                  value={overview.total_providers}
                  color="blue"
                />
                <StatCard
                  title="已启用"
                  value={overview.enabled_providers}
                  color="green"
                />
                <StatCard
                  title="API 密钥"
                  value={overview.total_keys}
                  subtitle={`${overview.enabled_keys} 启用`}
                  color="blue"
                />
                <StatCard
                  title="健康供应商"
                  value={overview.healthy_providers}
                  color="green"
                />
                <StatCard
                  title="熔断器开启"
                  value={overview.circuit_open_count}
                  color={overview.circuit_open_count > 0 ? 'red' : 'green'}
                />
                <StatCard
                  title="已启用密钥"
                  value={overview.enabled_keys}
                  color="blue"
                />
                <StatCard
                  title="配置状态"
                  value={overview.last_loaded ? '已加载' : '未加载'}
                  color={overview.last_loaded ? 'green' : 'yellow'}
                />
              </div>
            )}

            <div className="mb-6">
              <h2 className="text-lg font-medium text-gray-800 mb-4">
                供应商状态
              </h2>
              <ProviderStatusCards providers={providerStats} loading={loading} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div>
                <h2 className="text-lg font-medium text-gray-800 mb-4">
                  健康状态
                </h2>
                <HealthStatusChart providers={providerStats} loading={loading} />
              </div>
              <div>
                <h2 className="text-lg font-medium text-gray-800 mb-4">
                  配额总览
                </h2>
                <QuotaSummary
                  quotaData={quotaData as Record<string, Record<string, import('../services/searchAdminApi').SearchQuotaItem[]>> | null}
                  loading={loading}
                />
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
              <h2 className="text-lg font-medium text-gray-800 mb-4">
                用户配额查询
              </h2>
              <UserQuotaQueryPanel onQuery={handleUserQuotaQuery} loading={userQuotaLoading} />
              <div className="mt-4">
                <UserQuotaResultPanel data={userQuotaData} />
              </div>
            </div>
          </>
        )}

        {activeTab === 'trends' && (
          <TrendsTab />
        )}

        {activeTab === 'statistics' && (
          <SearchStatsDashboard
            autoRefresh={autoRefresh}
            refreshInterval={30000}
          />
        )}

        {activeTab === 'monitoring' && <SearchLiveMonitor />}

        {activeTab === 'settings' && (
          <SearchSettingsEditor onDataChange={fetchData} />
        )}

        {activeTab === 'quota' && (
          <SearchQuotaManager
            autoRefresh={autoRefresh}
            refreshInterval={30000}
          />
        )}

        {activeTab === 'alerts' && (
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg mb-2">告警配置</p>
            <p className="text-sm">
              告警功能开发中，敬请期待...
            </p>
          </div>
        )}

        <div className="mt-4 text-center text-sm text-gray-500">
          <button
            onClick={fetchData}
            className="text-blue-500 hover:text-blue-700"
          >
            立即刷新
          </button>
        </div>
      </div>
    </AdminLayout>
  );
};

const TrendsTab: React.FC = () => {
  const [trendData, setTrendData] = useState<
    Array<{
      provider_name: string;
      display_name: string;
      total_checks: number;
      total_failures: number;
      avg_latency_ms: number;
      enabled: boolean;
    }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchTrends = async () => {
      try {
        const result = await searchAdminApi.getTrends(undefined, 24);
        setTrendData(result.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : '获取趋势数据失败');
      } finally {
        setLoading(false);
      }
    };
    fetchTrends();
  }, []);

  if (loading) {
    return <div className="text-center py-8 text-gray-500">加载中...</div>;
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
        {error}
      </div>
    );
  }

  if (trendData.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">暂无趋势数据</div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-medium text-gray-800">供应商趋势 (24小时)</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50">
              <th className="text-left p-3 font-medium text-gray-600">
                供应商
              </th>
              <th className="text-left p-3 font-medium text-gray-600">状态</th>
              <th className="text-right p-3 font-medium text-gray-600">
                检查次数
              </th>
              <th className="text-right p-3 font-medium text-gray-600">
                失败次数
              </th>
              <th className="text-right p-3 font-medium text-gray-600">
                平均延迟
              </th>
            </tr>
          </thead>
          <tbody>
            {trendData.map((item, idx) => (
              <tr
                key={item.provider_name}
                className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
              >
                <td className="p-3 font-medium">
                  {item.display_name || item.provider_name}
                </td>
                <td className="p-3">
                  <span
                    className={`inline-flex items-center gap-1 text-xs ${
                      item.enabled ? 'text-green-600' : 'text-gray-400'
                    }`}
                  >
                    <span
                      className={`w-2 h-2 rounded-full ${
                        item.enabled ? 'bg-green-500' : 'bg-gray-400'
                      }`}
                    />
                    {item.enabled ? '启用' : '禁用'}
                  </span>
                </td>
                <td className="text-right p-3">{item.total_checks}</td>
                <td className="text-right p-3">
                  <span
                    className={
                      item.total_failures > 5 ? 'text-red-600 font-medium' : ''
                    }
                  >
                    {item.total_failures}
                  </span>
                </td>
                <td className="text-right p-3">
                  <span
                    className={
                      item.avg_latency_ms > 3000
                        ? 'text-red-600'
                        : item.avg_latency_ms > 1000
                          ? 'text-yellow-600'
                          : 'text-green-600'
                    }
                  >
                    {item.avg_latency_ms > 0
                      ? `${Math.round(item.avg_latency_ms)}ms`
                      : '-'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SearchAdminPage;
